import { useRef, useState, type ReactNode } from "react";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import type { QuickFillStatus } from "../types";
import { QuickFillFileValidator, QuickFillInputValidator } from "../validation";
import { DocumentAnalysisPanel } from "./DocumentAnalysisPanel";
import { FileChips } from "./FileChips";
import { PaperclipIcon, SparklesIcon, AlertIcon } from "./icons";
import { WizardPage } from "./WizardPage";

const inputValidator = new QuickFillInputValidator();
const fileValidator = new QuickFillFileValidator();

interface Props {
  text: string;
  files: File[];
  onTextChange: (text: string) => void;
  onFilesChange: (files: File[]) => void;
  onExtract: (text: string, files: File[]) => Promise<string[]>;
  onBack: () => void;
  backLabel?: ReactNode;
}

const ACCEPTED = ["image/", "application/pdf"];

function isAccepted(file: File): boolean {
  return ACCEPTED.some((p) => file.type.startsWith(p) || file.type === p);
}

export function QuickFill({
  text,
  files,
  onTextChange,
  onFilesChange,
  onExtract,
  onBack,
  backLabel,
}: Props) {
  const copy = useQuoteCopy();
  const [status, setStatus] = useState<QuickFillStatus>("idle");
  const [feedback, setFeedback] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const busy = status === "analyzing";
  const analyzingDocument = busy && files.length > 0;

  const clearFeedback = () => {
    if (status === "error") {
      setStatus("idle");
      setFeedback("");
    }
  };

  const addFiles = (incoming: FileList | File[]) => {
    const accepted = Array.from(incoming).filter(isAccepted);
    if (!accepted.length) return;
    const rejected = fileValidator.rejectReason(files, accepted, copy);
    if (rejected) {
      setStatus("error");
      setFeedback(rejected);
      return;
    }
    onFilesChange([...files, ...accepted]);
    clearFeedback();
  };

  const run = async () => {
    if (busy) return;
    const missing = inputValidator.validate(text, files, copy);
    if (missing) {
      setStatus("error");
      setFeedback(missing);
      return;
    }
    setStatus("analyzing");
    setFeedback(files.length > 0 ? "" : copy.t("qf.analyzing"));
    try {
      const filled = await onExtract(text, files);
      if (filled.length > 0) {
        onTextChange("");
        onFilesChange([]);
      } else {
        setStatus("error");
        setFeedback(copy.t("qf.noFields"));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg === "extract_cancelled") return;
      setStatus("error");
      if (msg === "rate_limited") {
        setFeedback(copy.t("qf.rateLimited"));
      } else if (msg === "forbidden") {
        setFeedback(copy.t("qf.blocked"));
      } else if (msg === "extraction_unavailable") {
        setFeedback(copy.t("qf.extractUnavailable"));
      } else {
        setFeedback(copy.t("qf.genericError"));
      }
    }
  };

  if (analyzingDocument) {
    return (
      <WizardPage
        title={copy.t("page.analyzing")}
        onBack={onBack}
        backLabel={backLabel}
        onContinue={run}
        showContinue={false}
      >
        <DocumentAnalysisPanel files={files} />
      </WizardPage>
    );
  }

  return (
    <WizardPage
      title={copy.t("page.quickFill")}
      onBack={onBack}
      backLabel={backLabel}
      onContinue={run}
      continueDisabled={busy}
      continueLabel={
        busy ? (
          <>
            <span className="nv-spinner" />
            {copy.t("qf.working")}
          </>
        ) : (
          <>
            <SparklesIcon size={16} />
            {copy.t("qf.fillForm")}
          </>
        )
      }
    >
      <div className="quickfill">
        <div className="qf-head">
          <span className="qf-badge">
            <SparklesIcon size={18} />
          </span>
          <span className="qf-title">{copy.t("page.quickFill")}</span>
        </div>
        <p className="qf-hint">{copy.t("qf.hint")}</p>

        <textarea
          className={`paste-area${dragOver ? " drag-over" : ""}`}
          value={text}
          aria-label={copy.t("qf.ariaPaste")}
          placeholder={copy.t("qf.placeholder")}
          onChange={(e) => {
            onTextChange(e.target.value);
            clearFeedback();
          }}
          aria-invalid={status === "error" && !text.trim() && files.length === 0}
          onPaste={(e) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            const dropped: File[] = [];
            for (let i = 0; i < items.length; i++) {
              if (items[i].kind === "file") {
                const f = items[i].getAsFile();
                if (f) dropped.push(f);
              }
            }
            if (dropped.length) addFiles(dropped);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
          }}
        />

        <FileChips files={files} onRemove={(i) => onFilesChange(files.filter((_, x) => x !== i))} />

        <div className="paste-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files?.length) addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            className="qf-attach-btn"
            title={copy.t("qf.attach")}
            aria-label={copy.t("qf.attach")}
            onClick={() => fileInputRef.current?.click()}
          >
            <PaperclipIcon size={17} />
          </button>
        </div>

        <p className="qf-disclaimer">{copy.t("qf.disclaimer")}</p>

        {feedback && (
          <div
            className={`paste-feedback${status === "error" ? " error" : ""}`}
            role={status === "error" ? "alert" : "status"}
          >
            {status === "analyzing" && <span className="nv-spinner" />}
            {status === "error" && <AlertIcon size={15} />}
            <span>{feedback}</span>
          </div>
        )}
      </div>
    </WizardPage>
  );
}
